import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAtom, useAtomValue } from "jotai";
import { callFunctionIfExists } from "../../../utils/GeneralUtils";
import { cloudColorTheme, tenantIDAtom } from "../../../atoms/GlobalAtoms";
import { AppConfirm } from "../../../utils/PopupUtil";
import { newToastEvent } from "../../../utils/ToastUtil";
import TenantAPI from "../../api/TenantAPI";
import useUser from "../auth/useUser";

const useRemoveUser = () => {
  const queryClient = useQueryClient();
  const tenantID = useAtomValue(tenantIDAtom);
  const { user: currentUser } = useUser();

  const removeUser = useMutation({
    mutationFn: ({
      user,
      invitation = false,
      supportTargetCloudID,
      leaveCloud = false,
      onSuccess,
    }: {
      user: any;
      invitation?: boolean;
      supportTargetCloudID: string | undefined;
      leaveCloud?: boolean;
      onSuccess?: () => void;
    }) => {
      return AppConfirm({
        title: invitation
          ? `Remove Invitation For ${user.PrimaryContactEmail}?`
          : leaveCloud
          ? `Leave Cloud?`
          : `Remove ${user.Name} From Cloud?`,
        text: invitation
          ? `Are you sure you want to remove the invite for ${user.PrimaryContactEmail}? This cannot be undone.`
          : leaveCloud
          ? "If you leave this cloud you will lose access to all workspaces and pages inside this cloud. You will also have to be reinvited to regain acess."
          : `Are you sure you want to remove  ${user.Name} from the cloud? This cannot be undone.`,
        confirmButtonText: "Remove",
      }).then((result) => {
        if (!result.isConfirmed) return;

        // Get previous users in case of error.
        const previousUsers = queryClient.getQueryData([
          "tenantUsers",
          tenantID,
        ]);

        // Optimistically update
        queryClient.setQueryData<any>(["tenantUsers", tenantID], (users: any) =>
          users?.filter((u: any) => u.id !== user.id)
        );

        return TenantAPI.removeUserInvite({
          userID: user.id,
          supportTargetCloudID: supportTargetCloudID,
        })
          .then((_) => {
            callFunctionIfExists(onSuccess);
            if (user.id === currentUser?.id) {
              queryClient.setQueryData(["clouds"], () => {
                const clouds = queryClient.getQueryData<Array<any>>(["clouds"]);
                if (!clouds) return;
                return clouds?.filter(
                  (cloud: any) => cloud.TenantID !== supportTargetCloudID
                );
              });
            }
            queryClient.setQueryData(["person", user.id], (prev: any) => {
              if (!prev) return prev;
              return {
                ...prev,
                Tenants: prev.Tenants.filter(
                  (tenant: any) => tenant.TenantID !== supportTargetCloudID
                ),
              };
            });

            newToastEvent(
              "success",
              `Successfully removed ${invitation ? "invite." : "user."}`
            );
          })
          .catch((err) => {
            console.error(err);
            newToastEvent(
              "error",
              `Failed to remove ${
                invitation ? "invitation." : "user."
              } Please try again.`
            );
            queryClient.setQueryData(["tenantUsers", tenantID], previousUsers);
          });
      });
    },
  });

  return {
    removeUser: removeUser.mutate,
    removingUser: removeUser.isPending,
    removeUserError: removeUser.error,
  };
};

export default useRemoveUser;
